import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Stack } from '@mui/material';
import { FilterAutocomplete } from 'atoms/TableFilter';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import Filter from 'organisms/Filter';
import { IFilterOption, IFilterOptionItem } from 'organisms/Filter/types';
import { GCPBillingCompanyFilter, GCPBillingInvoiceFilter } from '../types';

const localList =
  (items: IFilterOptionItem[]) => async (): Promise<IFilterOptionItem[]> =>
    items;

const FILTER_OPTIONS: IFilterOption[] = [
  {
    code: 'gcp_billing_month',
    title: 'Invoice Month',
    active: true,
  },
  {
    code: 'currency',
    title: 'Currency',
    fetchList: localList(
      ['USD', 'CAD', 'EUR', 'GBP', 'INR'].map((currency) => ({
        id: currency,
        name: currency,
      }))
    ),
  },
  {
    code: 'invoice_status',
    title: 'Invoice Status',
    selectionMode: 'single',
    fetchList: localList([
      { id: 'all', name: 'All' },
      { id: 'not-created', name: 'Invoice Not Created' },
      { id: 'created', name: 'Invoice Created' },
    ]),
  },
  {
    code: 'company_status',
    title: 'Company Status',
    selectionMode: 'single',
    fetchList: localList([
      { id: 'all', name: 'All' },
      { id: 'not-found', name: 'Company Not Found' },
      { id: 'found', name: 'Company Found' },
    ]),
  },
];

interface GCPBillingFiltersProps {
  month: string | null;
  gcpBillingId: string;
  currencies: string[];
  invoiceFilter: GCPBillingInvoiceFilter;
  companyFilter: GCPBillingCompanyFilter;
  onMonthChange: (value: string | null) => void;
  onGCPBillingIdChange: (value: string) => void;
  onCurrenciesChange: (value: string[]) => void;
  onInvoiceFilterChange: (value: GCPBillingInvoiceFilter) => void;
  onCompanyFilterChange: (value: GCPBillingCompanyFilter) => void;
}

type FilterValues = Record<string, string | string[] | undefined>;

const isInvoiceFilter = (value: string): value is GCPBillingInvoiceFilter =>
  value === 'not-created' || value === 'created';

const isCompanyFilter = (value: string): value is GCPBillingCompanyFilter =>
  value === 'not-found' || value === 'found';

const hasSameValues = (current: string[], next: string[]) =>
  current.length === next.length &&
  current.every((value, index) => value === next[index]);

const GCPBillingFilters: FC<GCPBillingFiltersProps> = ({
  month,
  gcpBillingId,
  currencies,
  invoiceFilter,
  companyFilter,
  onMonthChange,
  onGCPBillingIdChange,
  onCurrenciesChange,
  onInvoiceFilterChange,
  onCompanyFilterChange,
}) => {
  const ignoredInitialEmptyFilterChange = useRef(false);
  const currentFilters = useRef({
    month,
    currencies,
    invoiceFilter,
    companyFilter,
  });
  const currentCallbacks = useRef({
    onMonthChange,
    onCurrenciesChange,
    onInvoiceFilterChange,
    onCompanyFilterChange,
  });
  currentFilters.current = {
    month,
    currencies,
    invoiceFilter,
    companyFilter,
  };
  currentCallbacks.current = {
    onMonthChange,
    onCurrenciesChange,
    onInvoiceFilterChange,
    onCompanyFilterChange,
  };

  const filterExtend = useMemo(
    () => ({
      gcp_billing_month: month || '',
      currency: currencies,
      invoice_status: invoiceFilter === 'all' ? [] : [invoiceFilter],
      company_status: companyFilter === 'all' ? [] : [companyFilter],
    }),
    [companyFilter, currencies, invoiceFilter, month]
  );

  const handleFilterChange = useCallback((nextFilter: FilterValues) => {
    if (
      Object.keys(nextFilter).length === 0 &&
      !ignoredInitialEmptyFilterChange.current
    ) {
      ignoredInitialEmptyFilterChange.current = true;
      return;
    }

    const nextMonth =
      typeof nextFilter.gcp_billing_month === 'string' &&
      nextFilter.gcp_billing_month
        ? nextFilter.gcp_billing_month
        : null;
    const nextCurrencies = Array.isArray(nextFilter.currency)
      ? nextFilter.currency
      : [];
    const selectedInvoiceFilter = Array.isArray(nextFilter.invoice_status)
      ? nextFilter.invoice_status[0]
      : nextFilter.invoice_status;
    const nextInvoiceFilter =
      selectedInvoiceFilter && isInvoiceFilter(selectedInvoiceFilter)
        ? selectedInvoiceFilter
        : 'all';
    const selectedCompanyFilter = Array.isArray(nextFilter.company_status)
      ? nextFilter.company_status[0]
      : nextFilter.company_status;
    const nextCompanyFilter =
      selectedCompanyFilter && isCompanyFilter(selectedCompanyFilter)
        ? selectedCompanyFilter
        : 'all';
    const {
      month: currentMonth,
      currencies: currentCurrencies,
      invoiceFilter: currentInvoiceFilter,
      companyFilter: currentCompanyFilter,
    } = currentFilters.current;
    const {
      onMonthChange: setMonth,
      onCurrenciesChange: setCurrencies,
      onInvoiceFilterChange: setInvoiceFilter,
      onCompanyFilterChange: setCompanyFilter,
    } = currentCallbacks.current;

    if (nextMonth !== currentMonth) setMonth(nextMonth);
    if (!hasSameValues(currentCurrencies, nextCurrencies)) {
      setCurrencies(nextCurrencies);
    }
    if (nextInvoiceFilter !== currentInvoiceFilter) {
      setInvoiceFilter(nextInvoiceFilter);
    }
    if (nextCompanyFilter !== currentCompanyFilter) {
      setCompanyFilter(nextCompanyFilter);
    }
  }, []);

  return (
    <StyledFilterWrapper
      data-testid="gcp-billing-filter-toolbar"
      sx={{
        position: 'relative',
        '.filter-popover-container': { top: '40px', left: '5px' },
      }}
    >
      <Stack
        data-testid="gcp-billing-filter-controls"
        direction="row"
        gap={3}
        sx={{ minWidth: 0, width: '100%' }}
      >
        <Filter
          options={FILTER_OPTIONS}
          filterExtend={filterExtend}
          onChange={handleFilterChange}
        />
        <FilterAutocomplete
          isFreeSolo={Boolean(gcpBillingId)}
          value={gcpBillingId}
          options={[]}
          placeholder="Search by: GCP Billing ID"
          tooltip="Filter by GCP Billing ID"
          searchButtonAriaLabel="Search GCP Billing ID"
          inputAriaLabel="GCP Billing ID"
          resetButtonAriaLabel="Clear GCP Billing ID"
          autocompleteWidth="calc(100% - 24px)"
          data-testid="gcp-billing-id-search"
          sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: 304 }}
          onChange={(_, value) => onGCPBillingIdChange(value || '')}
          onReset={() => onGCPBillingIdChange('')}
        />
      </Stack>
    </StyledFilterWrapper>
  );
};

export default GCPBillingFilters;
